import { useState, useEffect } from 'react';
import { useUserRole } from '../hooks/useUserRole';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  SignedIn,
  SignedOut,
  SignInButton,
  UserButton,
} from '@clerk/clerk-react';
import {
  LayoutDashboard,
  Library,
  PenLine,
  FileText,
  FilePlus2,
  BarChart3,
  Settings,
  Sun,
  Moon,
  Menu,
  X,
  BookOpen,
  GraduationCap,
  ExternalLink,
  Lock,
  Mail,
  HelpCircle,
  Compass,
  Timer,
  History,
  ListChecks,
} from 'lucide-react';
import { AccessibilityModal } from './AccessibilityModal';
import { BanGate } from './BanGate';
import { AccessibilityIcon } from './AccessibilityIcon';
import { getA11yFabVisible } from '../utils/accessibility';

// Not: public/ klasorundeki dosyalar Vite'ta ES modulu olarak import edilemez
// (bitmiyo ana domaininde ayni hata daha once bulunup duzeltilmisti) — bu
// yuzden dogrudan string path kullaniliyor.
const logoUrl = '/logo_miyo.png';

// ─── Menü tanımı ───────────────────────────────────────────
const teacherMenuGroups = [
  {
    title: 'Genel Bakış',
    items: [
      { icon: Compass, label: 'Keşfet', path: '/kesfet' },
      { icon: LayoutDashboard, label: 'Pano', path: '/pano' },
      { icon: Timer, label: 'Odak', path: '/odak' },
    ],
  },
  {
    title: 'İçerik Stüdyosu',
    items: [
      { icon: PenLine, label: 'Soru Yaz', path: '/soru-yaz' },
      { icon: Library, label: 'Soru Havuzu', path: '/soru-havuzu' },
      { icon: FilePlus2, label: 'Test Oluştur', path: '/test-olustur' },
      { icon: FileText, label: 'Testlerim', path: '/testler' },
      { icon: BookOpen, label: 'Kitaplık', path: '/kitaplik' },
    ],
  },
  {
    title: 'Analiz',
    items: [
      { icon: BarChart3, label: 'Psikometri', path: '/analiz' },
      { icon: ListChecks, label: 'Soru Analizlerim', path: '/soru-analizleri' },
    ],
  },
];

const studentMenuGroups = [
  {
    title: 'Genel Bakış',
    items: [
      { icon: Compass, label: 'Keşfet', path: '/kesfet' },
      { icon: LayoutDashboard, label: 'Pano', path: '/pano' },
      { icon: FileText, label: 'Testler & Sınavlar', path: '/testler' },
      { icon: Timer, label: 'Odak', path: '/odak' },
      { icon: BarChart3, label: 'Gelişimim', path: '/analiz' },
      { icon: History, label: 'Çözüm Geçmişim', path: '/cozum-gecmisi' },
    ],
  },
];

// ─── Dark mode yönetimi ────────────────────────────────────
function useDarkMode() {
  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem('soru-dark-mode');
    return saved ? saved === 'true' : window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDark);
    localStorage.setItem('soru-dark-mode', String(isDark));
  }, [isDark]);

  return { isDark, toggle: (d: boolean) => setIsDark(!d) };
}

// ─── NavLink stil ─────────────────────────────────────────
function navLinkClass({ isActive }: { isActive: boolean }) {
  return [
    'flex items-center gap-3 px-3 py-2.5 rounded-lg font-medium text-sm transition-all',
    isActive
      ? 'bg-accent-100 dark:bg-accent-500/15 text-accent-600 dark:text-accent-500'
      : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white',
  ].join(' ');
}

// ─── Sidebar içeriği ──────────────────────────────────────
function SidebarContent({ onClose }: { onClose?: () => void }) {
  const { user, isTeacher } = useUserRole();
  const { isDark, toggle } = useDarkMode();

  const menuGroups = isTeacher ? teacherMenuGroups : studentMenuGroups;

  return (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="px-4 py-4 border-b border-slate-200 dark:border-slate-700/50">
        <Link
          to="/"
          onClick={onClose}
          className="flex items-center gap-2 group"
        >
          <img src={logoUrl} alt="Bitmiyo" className="h-10 w-10 object-contain shrink-0" />
          <span className="font-black text-lg text-slate-900 dark:text-white tracking-tight">
            Bitmiyo Soru
          </span>
        </Link>
      </div>

      {/* Rol badge */}
      <SignedIn>
        <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700/50">
          <div className="flex items-center gap-2">
            {isTeacher ? (
              <span className="soru-badge bg-accent-100 dark:bg-accent-500/15 text-accent-600 dark:text-accent-500">
                <GraduationCap size={10} className="mr-1" /> Öğretmen / Eğitmen
              </span>
            ) : (
              <span className="soru-badge bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400">
                Öğrenci
              </span>
            )}
          </div>
        </div>
      </SignedIn>

      {/* Navigasyon */}
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-5">
        {menuGroups.map((group) => (
          <div key={group.title}>
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500 px-3 mb-1.5">
              {group.title}
            </p>
            <div className="space-y-0.5">
              {group.items.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.path === '/'}
                  className={navLinkClass}
                  onClick={onClose}
                >
                  <div className="w-5 h-5 flex items-center justify-center shrink-0">
                    <item.icon size={17} className="opacity-80" />
                  </div>
                  {item.label}
                </NavLink>
              ))}
            </div>
          </div>
        ))}

        {/* Ana platforma dön */}
        <div className="pt-2">
          <a
            href="https://bitmiyo.com.tr"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-500 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 transition-all"
          >
            <ExternalLink size={15} className="opacity-70" />
            Ana Platforma Git
          </a>
        </div>
      </nav>

      {/* Alt alan: ayarlar + dark mode + auth */}
      <div className="px-3 py-4 border-t border-slate-200 dark:border-slate-700/50 space-y-2">
        <button
          onClick={() => toggle(isDark)}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all"
          aria-label="Tema değiştir"
        >
          {isDark ? <Sun size={17} /> : <Moon size={17} />}
          {isDark ? 'Açık Tema' : 'Koyu Tema'}
        </button>

        <SignedIn>
          <NavLink
            to="/ayarlar"
            className={navLinkClass}
            onClick={onClose}
          >
            <Settings size={17} className="opacity-80" />
            Ayarlar
          </NavLink>
        </SignedIn>

        <div className="px-3 py-2">
          <SignedIn>
            <div className="flex items-center gap-3">
              <UserButton
                afterSignOutUrl="/"
                appearance={{
                  elements: {
                    avatarBox: 'w-8 h-8 rounded-lg',
                  },
                }}
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-900 dark:text-white truncate">
                  {user?.firstName ?? 'Kullanıcı'}
                </p>
                <p className="text-xs text-slate-400 truncate">
                  {user?.primaryEmailAddress?.emailAddress}
                </p>
              </div>
            </div>
          </SignedIn>
          <SignedOut>
            <SignInButton mode="modal">
              <button className="soru-btn w-full bg-accent-500 hover:bg-accent-600 text-white">
                Giriş Yap
              </button>
            </SignInButton>
          </SignedOut>
        </div>
      </div>
    </div>
  );
}

// ─── Ana Layout ───────────────────────────────────────────
const PAGE_TITLES: Record<string, string> = {
  '/': 'Bitmiyo Soru | Öğretmenler için Soru Bankası, Öğrenciler için Anlık Geri Bildirim',
  '/kesfet': 'Testleri Keşfet | Bitmiyo Soru Portalı',
  '/pano': 'Pano | Bitmiyo Soru Portalı',
  '/odak': 'Odak | Bitmiyo Soru Portalı',
  '/soru-yaz': 'Soru Yaz | Bitmiyo Soru Portalı',
  '/soru-havuzu': 'Soru Havuzu | Bitmiyo Soru Portalı',
  '/test-olustur': 'Test Oluştur | Bitmiyo Soru Portalı',
  '/testler': 'Testlerim | Bitmiyo Soru Portalı',
  '/kitaplik': 'Kitaplık | Bitmiyo Soru Portalı',
  '/pdf-tara': 'PDF\'ten Test Hazırla | Bitmiyo Soru Portalı',
  '/analiz': 'Psikometri & Analiz | Bitmiyo Soru Portalı',
  '/ayarlar': 'Ayarlar | Bitmiyo Soru Portalı',
};

export function Layout() {
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [showA11yModal, setShowA11yModal] = useState(false);
  const [fabVisible, setFabVisible] = useState(getA11yFabVisible());
  const location = useLocation();

  // Kök path ("/") ve "/odak" herkese açık sayfalar — giriş yapılmamış
  // olsa bile normal sidebar kabuğu + Outlet gösterilmeli, aşağıdaki
  // "giriş gerekli" karşılama ekranı yerine.
  const isPublicRoute = location.pathname === '/' || location.pathname === '/odak' || location.pathname === '/kesfet';

  const currentTitle = PAGE_TITLES[location.pathname] ||
    (location.pathname.startsWith('/soru-yaz/') ? 'Soru Düzenle | Bitmiyo Soru Portalı' :
     location.pathname.startsWith('/test-olustur/') ? 'Test Düzenle | Bitmiyo Soru Portalı' :
     location.pathname.startsWith('/test/') ? 'Test Önizleme | Bitmiyo Soru Portalı' :
     'Bitmiyo Soru Portalı');

  // FAB görünürlüğünü güncelle (Settings sayfasından tetiklenebilir)
  useEffect(() => {
    const onStorage = () => setFabVisible(getA11yFabVisible());
    window.addEventListener('storage', onStorage);
    // Aynı sekme güncellemeleri için custom event
    window.addEventListener('a11y-fab-change', onStorage);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('a11y-fab-change', onStorage);
    };
  }, []);

  useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname]);

  return (
    <>
      <Helmet>
        <title>{currentTitle}</title>
      </Helmet>

      {/* Çapraz filigran logo — bitmiyo.com.tr ile aynı marka detayı */}
      <img
        src="/baslat.png"
        alt=""
        aria-hidden="true"
        style={{ transform: 'rotate(18deg)' }}
        className="fixed -bottom-24 right-4 w-[750px] h-[750px] object-contain opacity-[0.05] dark:opacity-[0.07] dark:invert pointer-events-none select-none -z-10 print:hidden"
      />

      {/* ── GİRİŞ YAPILMAMIŞSA YÖNETİM PORTALI KARŞILAMASI (NEO-BRUTALIST) ──
          Kök path ("/") herkese açık Keşfet sayfası olduğu için bu karşılama
          ekranı sadece korumalı rotalarda (soru-havuzu, testler, vb.) gösterilir. */}
      <SignedOut>
      {isPublicRoute ? null : (
        <div className="min-h-screen bg-[#fafafa] dark:bg-[#09090b] text-slate-900 dark:text-slate-100 flex flex-col justify-center items-center p-4 relative overflow-hidden font-sans">

          <div className="max-w-md w-full space-y-6 text-center relative z-10">
            {/* Gerçek Logo */}
            <div className="flex justify-center mb-2">
              <img
                src="/logo_miyo.png"
                alt="Bitmiyo Logo"
                className="h-16 md:h-20 w-auto object-contain drop-shadow-md"
              />
            </div>

            {/* Keskin Çerçeveli Hero Kartı */}
            <div className="bg-white dark:bg-slate-900 border-4 border-slate-900 dark:border-slate-100 p-8 shadow-[8px_8px_0px_0px_rgba(15,23,42,1)] dark:shadow-[8px_8px_0px_0px_rgba(241,245,249,1)] text-left space-y-4">
              <div className="inline-block bg-amber-400 text-slate-950 font-black text-[10px] tracking-widest uppercase px-2.5 py-1 border border-slate-900">
                Soru & Test Yönetim Portalı
              </div>
              
              <h1 className="text-2xl md:text-3xl font-black tracking-tight text-slate-900 dark:text-white uppercase leading-none">
                Öğretmen & İçerik Stüdyosu
              </h1>

              <p className="text-xs font-semibold text-slate-600 dark:text-slate-400 leading-relaxed border-l-2 border-slate-900 dark:border-slate-100 pl-3">
                Soru bankası, içerik stüdyosu ve analiz modüllerine erişim sağlamak için yetkilendirilmiş hesabınızla oturum açın.
              </p>
            </div>

            {/* Keskin Buton */}
            <div className="pt-2">
              <SignInButton mode="modal">
                <button className="w-full flex items-center justify-center gap-2 bg-accent-500 hover:bg-accent-600 text-white border-2 border-accent-600 py-3.5 px-6 font-bold text-sm tracking-widest uppercase shadow-[4px_4px_0px_0px_rgba(13,148,136,1)] hover:shadow-none hover:translate-x-1 hover:translate-y-1 transition-all">
                  <Lock size={16} strokeWidth={2.5} /> Sisteme Giriş Yap
                </button>
              </SignInButton>
            </div>

            {/* Keskin Teknik Uyarı Kutusu */}
            <div className="p-5 bg-amber-50/50 dark:bg-slate-900/50 border-2 border-amber-900 dark:border-slate-700 text-left space-y-3 relative">
              <div className="absolute -left-2 -top-2 w-4 h-4 bg-amber-400 border-2 border-amber-900"></div>
              <div className="flex items-center gap-2 text-amber-900 dark:text-amber-500 font-black text-xs tracking-wider uppercase pl-2">
                <HelpCircle size={15} strokeWidth={2.5} />
                <span>Erişim Desteği</span>
              </div>
              <p className="text-xs font-medium text-amber-900/80 dark:text-slate-400 leading-relaxed pl-2">
                Eğitmen yetkisi atanmış olmasına rağmen panele erişimde sorun yaşıyorsanız, sistem yöneticisiyle iletişime geçiniz:
              </p>
              <div className="ml-2 inline-flex bg-white dark:bg-slate-950 border-2 border-amber-900/20 dark:border-slate-800 px-3 py-1.5 items-center gap-2 text-xs font-mono font-bold text-slate-900 dark:text-amber-200">
                <Mail size={14} />
                <a href="mailto:bilgi@bitmiyo.com.tr" className="hover:text-accent-600">
                  bilgi@bitmiyo.com.tr
                </a>
              </div>
            </div>

            {/* Keskin Alt Link */}
            <div className="pt-6 border-t-2 border-slate-100 dark:border-slate-800">
              <a
                href="https://bitmiyo.com.tr"
                className="text-[11px] font-bold text-slate-400 hover:text-slate-900 dark:hover:text-white uppercase tracking-[0.2em] inline-flex items-center gap-2 transition-colors group"
              >
                <ExternalLink size={14} className="group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" /> 
                Ana Platforma Dön
              </a>
            </div>
          </div>
        </div>
      )}
      </SignedOut>

      {/* ── GİRİŞ YAPILMIŞSA TAM ARAYÜZ, VEYA "/" HERKESE AÇIK KEŞFET SAYFASI ── */}
      {isPublicRoute && (
        <SignedOut>
          <div className="flex min-h-screen relative">
            {/* Desktop Sidebar */}
            <aside className="hidden md:flex flex-col w-64 shrink-0 h-screen sticky top-0 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl border-r border-slate-200 dark:border-slate-700/50 print:hidden">
              <SidebarContent />
            </aside>

            {/* Mobile Overlay */}
            {isMobileOpen && (
              <div
                className="fixed inset-0 z-40 bg-black/40 md:hidden"
                onClick={() => setIsMobileOpen(false)}
              />
            )}

            {/* Mobile Drawer */}
            <aside
              className={[
                'fixed inset-y-0 left-0 z-50 w-72 flex flex-col md:hidden print:hidden',
                'bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-700/50',
                'transition-transform duration-300',
                isMobileOpen ? 'translate-x-0' : '-translate-x-full',
              ].join(' ')}
            >
              <button
                onClick={() => setIsMobileOpen(false)}
                className="absolute top-4 right-4 p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              >
                <X size={18} />
              </button>
              <SidebarContent onClose={() => setIsMobileOpen(false)} />
            </aside>

            {/* Main Content */}
            <div className="flex-1 flex flex-col min-w-0">
              {/* Mobile topbar */}
              <header className="md:hidden print:hidden sticky top-0 z-30 flex items-center gap-3 px-4 py-3 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl border-b border-slate-200 dark:border-slate-700/50">
                <button
                  onClick={() => setIsMobileOpen(true)}
                  className="p-2 rounded-lg text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                  aria-label="Menü aç"
                >
                  <Menu size={20} />
                </button>
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 bg-accent-500 rounded-md flex items-center justify-center">
                    <BookOpen size={12} className="text-white" />
                  </div>
                  <span className="text-sm font-black text-slate-900 dark:text-white tracking-tight">
                    Bitmiyo Soru
                  </span>
                </div>
              </header>

              {/* Page content */}
              <main className="flex-1">
                <Outlet />
              </main>
            </div>
          </div>
        </SignedOut>
      )}

      <SignedIn>
        <BanGate>
        {/* Not: arkaplan rengi bilerek burada değil, body'de (index.css) tanımlı —
            aksi halde bu opak div, fixed konumlu filigran kartalı (üstteki <img>)
            tamamen örtüyordu. */}
        <div className="flex min-h-screen relative">

          {/* Desktop Sidebar */}
          <aside className="hidden md:flex flex-col w-64 shrink-0 h-screen sticky top-0 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl border-r border-slate-200 dark:border-slate-700/50 print:hidden">
            <SidebarContent />
          </aside>

          {/* Mobile Overlay */}
          {isMobileOpen && (
            <div
              className="fixed inset-0 z-40 bg-black/40 md:hidden"
              onClick={() => setIsMobileOpen(false)}
            />
          )}

          {/* Mobile Drawer */}
          <aside
            className={[
              'fixed inset-y-0 left-0 z-50 w-72 flex flex-col md:hidden print:hidden',
              'bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-700/50',
              'transition-transform duration-300',
              isMobileOpen ? 'translate-x-0' : '-translate-x-full',
            ].join(' ')}
          >
            <button
              onClick={() => setIsMobileOpen(false)}
              className="absolute top-4 right-4 p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            >
              <X size={18} />
            </button>
            <SidebarContent onClose={() => setIsMobileOpen(false)} />
          </aside>

          {/* Main Content */}
          <div className="flex-1 flex flex-col min-w-0">
            {/* Mobile topbar */}
            <header className="md:hidden print:hidden sticky top-0 z-30 flex items-center gap-3 px-4 py-3 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl border-b border-slate-200 dark:border-slate-700/50">
              <button
                onClick={() => setIsMobileOpen(true)}
                className="p-2 rounded-lg text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                aria-label="Menü aç"
              >
                <Menu size={20} />
              </button>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 bg-accent-500 rounded-md flex items-center justify-center">
                  <BookOpen size={12} className="text-white" />
                </div>
                <span className="text-sm font-black text-slate-900 dark:text-white tracking-tight">
                  Bitmiyo Soru
                </span>
              </div>
              <div className="ml-auto">
                <UserButton
                  afterSignOutUrl="/"
                  appearance={{ elements: { avatarBox: 'w-7 h-7 rounded-md' } }}
                />
              </div>
            </header>

            {/* Page content */}
            <main className="flex-1">
              <Outlet />
            </main>
          </div>

          {/* Yüzen Erişilebilirlik Butonu (Floating Widget) */}
          {fabVisible && (
            <button
              type="button"
              onClick={() => setShowA11yModal(true)}
              className="fixed bottom-5 right-5 z-40 w-12 h-12 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-2xl flex items-center justify-center shadow-2xl transition-all hover:scale-105 active:scale-95 border border-slate-200 dark:border-slate-600 print:hidden"
              title="Erişilebilirlik Ayarlarını Aç"
            >
              <AccessibilityIcon size={24} />
            </button>
          )}
        </div>

        <AccessibilityModal isOpen={showA11yModal} onClose={() => setShowA11yModal(false)} />
        </BanGate>
      </SignedIn>
    </>
  );
}
